import React, { useCallback, useEffect, useMemo, useState } from "react";
import { compose } from "recompose";
import { inject } from "mobx-react";
import { reaction } from "mobx";
import PrintOutlined from "@material-ui/icons/PrintOutlined";
import { useSnackbar } from "notistack";

import ToolbarIconWithLoader from "../../../../common/ToolbarIconWithLoader";
import { withUserProfile } from "@withUserProfile";
import { usePhysicalDrPreview } from "./hooks/usePhysicalDrPreview";
import { getPhysicalDrTooltipTitle, PHYSICAL_DR_CONTENT } from "./content";
import {
  resolvePhysicalDrRouteContext,
  type RoutePrintContext,
} from "./utils/getRoutePrintContext";

interface Props {
  eventHub: any;
  headerId: string;
  userData: any;
  isClosedTrailersGrid?: boolean;
  cachedGridApi?: any;
  DispatchStore?: any;
}

const PrintPhysicalDrHeaderControl = ({
  eventHub,
  headerId,
  userData,
  isClosedTrailersGrid = false,
  cachedGridApi,
  DispatchStore,
}: Props) => {
  const { enqueueSnackbar } = useSnackbar();
  const [gridApi, setGridApi] = useState<any>(null);
  const [selectionVersion, setSelectionVersion] = useState(0);
  const preview = usePhysicalDrPreview({
    onFailure: () =>
      enqueueSnackbar(PHYSICAL_DR_CONTENT.snackbar.error, { variant: "error" }),
  });

  const terminalCode = userData?.userProfile?.terminal?.code;

  const bumpSelectionVersion = useCallback(() => {
    setSelectionVersion((version) => version + 1);
  }, []);

  useEffect(() => {
    const handleGridApiRegistered = (api: any) => {
      if (!api || typeof api.getSelectedRows !== "function") {
        setGridApi(null);
        return;
      }
      setGridApi(api);
      bumpSelectionVersion();
    };

    const gridApiRegistrationEvent = `${headerId}_setGridApi`;
    eventHub.on(gridApiRegistrationEvent, handleGridApiRegistered);
    if (
      cachedGridApi &&
      typeof cachedGridApi.getSelectedRows === "function"
    ) {
      handleGridApiRegistered(cachedGridApi);
    }
    return () => {
      eventHub.off(gridApiRegistrationEvent, handleGridApiRegistered);
    };
  }, [eventHub, headerId, cachedGridApi, bumpSelectionVersion]);

  useEffect(() => {
    if (!gridApi) {
      return undefined;
    }
    gridApi.addEventListener("selectionChanged", bumpSelectionVersion);
    gridApi.addEventListener("rowDataChanged", bumpSelectionVersion);
    gridApi.addEventListener("rowDataUpdated", bumpSelectionVersion); // Drivers grid uses deltaRowDataMode; row data updates emit rowDataUpdated instead of rowDataChanged.
    return () => {
      gridApi.removeEventListener("selectionChanged", bumpSelectionVersion);
      gridApi.removeEventListener("rowDataChanged", bumpSelectionVersion);
      gridApi.removeEventListener("rowDataUpdated", bumpSelectionVersion);
    };
  }, [gridApi, bumpSelectionVersion]);

  useEffect(() => {
    if (!DispatchStore || isClosedTrailersGrid) {
      return undefined;
    }
    return reaction(
      () => DispatchStore.selectedDriverRouteId,
      () => {
        bumpSelectionVersion();
      }
    );
  }, [DispatchStore, isClosedTrailersGrid, bumpSelectionVersion]);

  const { printRouteContext, selectedRowCount } = useMemo(() => {
    if (!gridApi) {
      return { printRouteContext: null as RoutePrintContext | null, selectedRowCount: 0 };
    }
    const selectedRows = gridApi.getSelectedRows?.() ?? [];
    const selectedRowCount = selectedRows.length;
    const driverId = selectedRows[0]?.driverId;
    const selectedDriverRouteId = DispatchStore?.selectedDriverRouteId ?? null;
    const driverRoutes =
      !isClosedTrailersGrid && driverId && DispatchStore
        ? DispatchStore.getDriverRoutes(driverId)
        : [];

    return {
      selectedRowCount,
      printRouteContext: resolvePhysicalDrRouteContext(selectedRows, {
        isClosedTrailersGrid,
        selectedDriverRouteId,
        driverRoutes,
      }),
    };
  }, [
    gridApi,
    selectionVersion,
    isClosedTrailersGrid,
    DispatchStore,
  ]);

  const isPrintEnabled = Boolean(
    terminalCode &&
      printRouteContext?.routeId &&
      printRouteContext.hasPhysicalDrNeed
  );
  const tooltipTitle = getPhysicalDrTooltipTitle({
    isGenerating: preview.isGenerating,
    terminalCode,
    routeId: printRouteContext?.routeId,
    hasPhysicalDrNeed: Boolean(printRouteContext?.hasPhysicalDrNeed),
    selectedRowCount,
  });

  const handlePrintClick = () => {
    if (
      isPrintEnabled &&
      printRouteContext?.routeId &&
      !preview.isGenerating
    ) {
      preview.startPreview(terminalCode, [printRouteContext.routeId]);
    }
  };

  return (
    <ToolbarIconWithLoader
      icon={<PrintOutlined />}
      enabled={isPrintEnabled}
      loading={preview.isGenerating}
      tooltipTitle={tooltipTitle}
      onClick={handlePrintClick}
      rootClassName="glHeaderIcons"
    />
  );
};

export default compose(
  withUserProfile,
  inject("DispatchStore")
)(PrintPhysicalDrHeaderControl as React.ComponentType<any>);
